/* @flow */
import type { MessageType } from 'chat/lib/MessageInterfacePropType';

export type MessageBunch = {
  date: Date,
  senderId?: number,
  messageType: string,
  messages: MessageType[],
};

export const sameDay = (a: Date, b: ?Date): boolean => (
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()
);

function makeBunch(message: MessageType): MessageBunch {
  const date = message.getSentAt();
  const sender = message.getSender();
  return {
    date,
    senderId: sender ? sender.id : undefined,
    messageType: message.getType(),
    messages: [message],
  };
}

const MESSAGE_BUNCH_TIME_WINDOW = 1000 * 60 * 5; // 5 min

/**
 * A bunch is of the same type, on same day, from same sender, within MESSAGE_BUNCH_TIME_WINDOW.
 */
function messageFits(message: MessageType, bunch: MessageBunch): boolean {
  if (message.getType() !== bunch.messageType) {
    return false;
  }
  if (!sameDay(message.getSentAt(), bunch.date)) {
    return false;
  }
  if (message.getSender().id !== bunch.senderId) {
    return false;
  }
  if (message.getType() !== 'system' && message.getSentAt() - bunch.date > MESSAGE_BUNCH_TIME_WINDOW) {
    return false;
  }
  return true;
}

function bunchMessages(messages: MessageType[]): MessageBunch[] {
  if (messages.length === 0) {
    return [];
  }

  const messageStack = Array.from(messages).reverse();

  const bunches = [
    makeBunch(messageStack.pop()),
  ];

  while (messageStack.length > 0) {
    const currBunch = bunches[bunches.length - 1];
    const currMessage = messageStack.pop();

    if (messageFits(currMessage, currBunch)) {
      currBunch.messages.push(currMessage);
    } else {
      bunches.push(makeBunch(currMessage));
    }
  }

  return bunches;
}

export function extractGapAndBunches(messages: MessageType[]) {
  const gapMessage = (messages.length > 0 && messages[0].getType() === 'gap') ? messages[0] : null;
  const fullMessages = gapMessage ? messages.slice(1) : messages;
  const bunches = bunchMessages(fullMessages);
  return { gapMessage, bunches };
}

export function extractListItems(messages: MessageType[]) {
  const { gapMessage, bunches } = extractGapAndBunches(messages);

  const items = [];

  if (gapMessage) {
    // no date to render before gap because we don't know what date the message
    // represented by the gap was on until we fetch it.
    items.push(gapMessage);
  }

  let bunchDate = null;
  for (const bunch: MessageBunch of bunches) {
    if (!sameDay(bunch.date, bunchDate)) {
      bunchDate = bunch.date;
      items.push(bunchDate);
    }
    items.push(bunch);
  }

  return items;
}



// WEBPACK FOOTER //
// ./src/chat/lib/MessageHistory.js